import { Link } from 'react-router-dom'
import {
  LayoutDashboard, Users, Building2, Star, FileText, Mail,
  Clapperboard, TrendingUp, Lightbulb, CheckSquare, DollarSign,
  Settings, ChevronRight, ChevronLeft, PhoneCall, Calendar, X, LogOut, Brain, BarChart3,
  HardDrive, Newspaper, Film, UserPlus, Inbox, BookOpen, Search, Flame, Handshake, Wrench,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { MONDRIAN_MODULES, ROTHKO_MODULES, BLOCKS, BLOCK_NAV, RAIL_BLOCK_ORDER } from '../../config/themes'
import { entityLabel } from '../../lib/demoClient'

// Left rail = one entry per record Block. Clicking opens the Block's landing
// page; its sections render as tabs across the top (BlockTabBar). Universal is
// NOT here — its tabs live in the always-visible UniversalBar. Labels + landing
// routes come from BLOCK_NAV; these are just the rail glyphs.
const BLOCK_ICONS = {
  universal: LayoutDashboard,
  directory: Users,
  management: Star,
  production: Clapperboard, // the merged Production block (registry + studio)
  deals: Handshake,
  finance: DollarSign,
  research: Search,
  engineering: Wrench,
  system: Settings,
}

export default function Sidebar({ isMobile, isOpen, onClose, collapsed, onToggleCollapse }) {
  const { user, profile, signOut } = useAuth()
  const { theme, module, block: activeBlock } = useTheme()

  if (isMobile && !isOpen) return null

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User'
  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U'

  const width = isMobile ? 'w-64' : collapsed ? 'w-16' : 'w-56'

  const isChatrone = theme.startsWith('chatrone')
  const isDark = theme === 'rothko' || theme === 'chatrone-dark'
  const isBlocks = theme === 'blocks'

  // Theme-aware sidebar shell styles
  const sidebarStyle = (() => {
    if (theme === 'blocks') return { background: '#FCFBF9', borderRight: '1px solid #ECEAE3' }
    if (theme === 'mondrian') return { borderRight: '3.5px solid #1A1A2E', background: '#FFFFFF' }
    if (theme === 'rothko') {
      const r = ROTHKO_MODULES[module] || ROTHKO_MODULES.default
      return { borderRight: '1px solid rgba(255,255,255,0.06)', background: r.bg }
    }
    if (theme === 'chatrone-dark') return { background: '#0C141E', borderRight: 'none' }
    if (isChatrone) return { background: '#2D3E4E', borderRight: 'none' }
    return {}
  })()

  // Logo section border
  const logoSectionStyle = (() => {
    if (theme === 'blocks') return { borderBottom: '1px solid #ECEAE3' }
    if (theme === 'mondrian') return { borderBottom: '3.5px solid #1A1A2E' }
    if (theme === 'rothko') return { borderBottom: '1px solid rgba(255,255,255,0.06)' }
    if (isChatrone) return { borderBottom: '1px solid rgba(255,255,255,0.10)' }
    return {}
  })()

  // Text color overrides for dark-sidebar themes
  const textStyle = isDark || isChatrone ? { color: 'rgba(255,255,255,0.70)' } : {}
  const dimTextStyle = isDark || isChatrone ? { color: 'rgba(255,255,255,0.35)' } : {}

  return (
    <aside
      data-shell="sidebar"
      className={`
        ${isMobile
          ? 'fixed left-0 top-0 h-screen w-64 z-50 animate-slide-in'
          : `fixed left-0 top-0 h-screen ${width} z-40 transition-all duration-200`
        }
        bg-white border-r border-warm-300 flex flex-col shadow-sm
      `}
      style={sidebarStyle}
    >
      {/* Logo + collapse toggle */}
      <div
        // Collapsed, the rail is too narrow for the mark and the toggle side by
        // side (the mark was squeezed to a sliver) — stack them instead.
        className={`${collapsed ? 'px-3 py-4 flex-col gap-2' : 'px-5 py-5 justify-between'} border-b border-warm-200 flex items-center`}
        style={logoSectionStyle}
      >
        <div className={`flex items-center gap-2.5 ${collapsed ? '' : 'min-w-0'}`}>
          {/* Icon mark — always visible, used as the collapsed logo */}
          <img
            src="/logo-square.png"
            alt="Chatrone"
            className="shrink-0 rounded-lg"
            style={{ width: 28, height: 28, maxWidth: 'none', objectFit: 'contain' }}
          />
          {/* Wordmark — typeset, not the PNG: the PNG carries its own cat mark
              (a second logo beside the square one) and pale lettering that
              vanishes on the light sidebar when shrunk to 20px. */}
          {!collapsed && (
            <span
              className="text-[15px] font-bold tracking-[0.14em] truncate"
              style={{ color: isChatrone || theme === 'rothko' || theme === 'midnight' ? '#38c9fd' : '#27272a' }}
            >
              CHATRONE
            </span>
          )}
        </div>
        {isMobile ? (
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-colors ml-auto"
            style={isChatrone || isDark ? { color: 'rgba(255,255,255,0.4)' } : { color: '#71717a' }}
          >
            <X size={18} />
          </button>
        ) : (
          <button
            onClick={onToggleCollapse}
            className={`p-1 rounded-lg transition-colors ${collapsed ? '' : 'ml-auto'}`}
            style={isChatrone || isDark ? { color: 'rgba(255,255,255,0.4)' } : { color: '#71717a' }}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>

      {/* Nav — one colored tile per record Block (sections live in the top tab
          bar; Universal lives in the UniversalBar, not here) */}
      <nav className="flex-1 px-2 py-4 overflow-y-auto space-y-1.5">
        {RAIL_BLOCK_ORDER.map(blockId => (
          <BlockRailItem
            key={blockId}
            blockId={blockId}
            active={activeBlock === blockId}
            collapsed={collapsed && !isMobile}
            onNavigate={isMobile ? onClose : undefined}
            theme={theme}
          />
        ))}
      </nav>

      {/* User + Entity */}
      <div
        className={`${collapsed ? 'px-2' : 'px-4'} py-3 border-t space-y-2`}
        style={
          theme === 'mondrian' ? { borderTop: '3.5px solid #1A1A2E' }
          : isDark || isChatrone ? { borderTop: '1px solid rgba(255,255,255,0.08)' }
          : { borderTop: '1px solid #e5e7eb' }
        }
      >
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2 px-1'}`}>
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
            style={isChatrone || isDark
              ? { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)' }
              : { background: '#EAF0F7', border: '1px solid #D5E2F0' }
            }
          >
            <span
              className="text-[10px] font-bold"
              style={isChatrone || isDark ? { color: 'rgba(255,255,255,0.8)' } : { color: '#2C4A6B' }}
            >{initials}</span>
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate" style={textStyle || { color: '#27272a' }}>{displayName}</p>
                <p className="text-[10px] truncate" style={dimTextStyle || { color: '#71717a' }}>{user?.email}</p>
              </div>
              <button
                onClick={signOut}
                title="Sign out"
                className="p-1.5 rounded-lg transition-colors"
                style={dimTextStyle || { color: '#71717a' }}
              >
                <LogOut size={14} />
              </button>
            </>
          )}
        </div>

        {!collapsed && (
          <div
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
            style={isChatrone || isDark
              ? { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.08)' }
              : theme === 'mondrian'
              ? { background: '#f5f5f5', border: '2px solid #1A1A2E' }
              : { background: '#F4F6F8', border: '1px solid #E4E8ED' }
            }
          >
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ background: profile?.company_id === 'chatrone_latam' ? '#E85B40' : '#3DD5CF' }}
            />
            <span className="text-[10px] font-mono" style={dimTextStyle || { color: '#71717a' }}>
              {entityLabel(profile?.company_id)}
            </span>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center py-1">
            <div className="w-2 h-2 rounded-full bg-sage-400" title={profile?.company_id || 'chatrone_us'} />
          </div>
        )}
      </div>
    </aside>
  )
}

function BlockRailItem({ blockId, active, collapsed, onNavigate }) {
  const nav = BLOCK_NAV[blockId]
  const blk = BLOCKS[blockId] || BLOCKS.default
  const Icon = BLOCK_ICONS[blockId] || LayoutDashboard
  const label = nav?.label || blockId

  // Each Block is a solid colored tile in its own accent. The active Block gets
  // an inset white ring + a touch of elevation; the rest sit slightly dimmed so
  // the current section pops out of the column of color blocks.
  const tileStyle = {
    background: blk.accent,
    color: '#FFFFFF',
    boxShadow: active
      ? 'inset 0 0 0 2px rgba(255,255,255,0.7), 0 1px 4px rgba(0,0,0,0.18)'
      : undefined,
  }
  const stateClass = active ? 'opacity-100' : 'opacity-[0.82] hover:opacity-100'

  if (collapsed) {
    return (
      <Link
        to={nav.landing}
        onClick={onNavigate}
        title={label}
        data-active={active ? 'true' : undefined}
        className={`relative flex items-center justify-center h-10 rounded-lg transition-opacity ${stateClass}`}
        style={tileStyle}
      >
        <Icon size={18} />
      </Link>
    )
  }

  return (
    <Link
      to={nav.landing}
      onClick={onNavigate}
      data-active={active ? 'true' : undefined}
      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-opacity ${stateClass}`}
      style={tileStyle}
    >
      <Icon size={18} className="shrink-0" />
      <span className="text-sm font-semibold flex-1">{label}</span>
    </Link>
  )
}
